import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Bot,
  User,
  Plus,
  ChevronLeft,
  Loader2,
  Sparkles,
  Target,
  TrendingUp,
  BookOpen,
  AlertCircle,
  MessageSquare,
  Zap,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  mentorChat,
  getMentorConversations,
  getMentorConversation,
  updateMentorGoal,
  getMentorGapAnalysis,
} from '../../api/client';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

interface Message {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  referenced_courses?: Array<{
    id: number;
    title: string;
    slug: string;
    price: number;
  }>;
  created_at?: string;
}

interface Conversation {
  id: number;
  title: string;
  is_pinned: number;
  updated_at: string;
}

interface GapAnalysis {
  identified_gaps: string[];
  recommended_courses: Array<{
    course_id: number;
    course_title: string;
    gap: string;
  }>;
  enrolled_courses: Array<{
    id: number;
    title: string;
    progress_percent: number;
  }>;
}

const SUGGESTIONS = [
  'What AI skills should I learn to reach ₹15L PA?',
  'Review my career gap between Data Analyst and ML Engineer',
  'Which course should I take first?',
  'Help me prepare for a Google interview',
  'Is my current salary fair for my role?',
];

export default function MentorChatScreen(): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialConvId = searchParams.get('c') ? parseInt(searchParams.get('c')!, 10) : null;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(initialConvId);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysis | null>(null);
  const [isLoadingGap, setIsLoadingGap] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    loadGapAnalysis();
  }, []);

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversationId) {
      loadConversation(activeConversationId);
    } else {
      setMessages([]);
      setShowOnboarding(false);
    }
  }, [activeConversationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Handle Enter to send, Shift+Enter for new line
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const loadConversations = async () => {
    try {
      const res = await getMentorConversations();
      if (res.conversations) {
        setConversations(res.conversations);
      }
    } catch {
      // silent
    }
  };

  const loadConversation = async (id: number) => {
    setIsLoadingHistory(true);
    try {
      const res = await getMentorConversation(id);
      if (res.conversation?.messages) {
        const mapped: Message[] = res.conversation.messages.map((m: any) => ({
          id: m.id,
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          referenced_courses: m.referenced_course_ids,
          created_at: m.created_at,
        }));
        setMessages(mapped);
      }
    } catch {
      setError('Failed to load conversation');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadGapAnalysis = async () => {
    setIsLoadingGap(true);
    try {
      const res = await getMentorGapAnalysis();
      if (res.identified_gaps) {
        setGapAnalysis(res as GapAnalysis);
      }
    } catch {
      // silent
    } finally {
      setIsLoadingGap(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setError('');

    const tempUserMsg: Message = { role: 'user', content: userMessage };
    setMessages((prev) => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      const res = await mentorChat({
        message: userMessage,
        conversation_id: activeConversationId ?? undefined,
      });

      if (res.reply) {
        const assistantMsg: Message = {
          role: 'assistant',
          content: res.reply,
          referenced_courses: res.referenced_courses,
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (res.conversation_id && !activeConversationId) {
          setActiveConversationId(res.conversation_id);
          await loadConversations();
        }
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to send message';
      setError(msg);
      // Remove the temp user message on error so they can retry
      setMessages((prev) => prev.slice(0, -1));
      setInput(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setMessages([]);
    setError('');
    setShowOnboarding(false);
    if (window.innerWidth < 768) {
      setShowSidebar(false);
    }
  };

  const startOnboarding = () => {
    setShowOnboarding(true);
  };

  const submitGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await updateMentorGoal({
        current_role: fd.get('current_role') as string,
        target_role: fd.get('target_role') as string,
        years_experience: parseFloat(fd.get('years_experience') as string) || 0,
        career_goal_summary: fd.get('career_goal_summary') as string,
      });
      setShowOnboarding(false);
      setGapAnalysis(null);
      await loadGapAnalysis();
      // Auto-send a welcome message
      setInput('Analyze my career gaps and tell me what to learn first.');
    } catch {
      setError('Failed to save goal');
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-slate-50">
      {/* Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="hidden md:flex flex-col border-r border-slate-200 bg-white"
          >
            <div className="p-4 border-b border-slate-100">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={handleNewChat}
              >
                New Chat
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => {
                    setActiveConversationId(conv.id);
                    setShowSidebar(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors flex items-center gap-2 ${
                    activeConversationId === conv.id
                      ? 'bg-amber-50 text-amber-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 shrink-0 opacity-60" />
                  <span className="truncate">{conv.title}</span>
                </button>
              ))}
              {conversations.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No conversations yet
                </div>
              )}
            </div>

            {/* Gap Analysis mini-card */}
            {gapAnalysis && gapAnalysis.identified_gaps.length > 0 && (
              <div className="p-3 border-t border-slate-100">
                <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Your Gaps
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {gapAnalysis.identified_gaps.slice(0, 4).map((gap, i) => (
                      <span
                        key={i}
                        className="text-[10px] px-2 py-0.5 bg-white border border-slate-200 rounded-full text-slate-600"
                      >
                        {gap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-14 border-b border-slate-200 bg-white/80 backdrop-blur flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-3">
            {!showSidebar && (
              <button
                onClick={() => setShowSidebar(true)}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 md:hidden"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-slate-900">Mentor</h1>
                <p className="text-[10px] text-slate-500 leading-none">AI Career Companion</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="hidden md:flex"
              leftIcon={<Target className="w-4 h-4" />}
              onClick={startOnboarding}
            >
              Set Goal
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
          {messages.length === 0 && !isLoadingHistory && (
            <div className="max-w-xl mx-auto space-y-8">
              {/* Welcome */}
              <div className="text-center space-y-3">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
                  <Bot className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Hey {user?.name?.split(' ')[0] || 'there'}, I'm Mentor
                </h2>
                <p className="text-slate-500 max-w-sm mx-auto">
                  Your AI career strategist. I know your profile, your goals, and our courses. Ask me anything.
                </p>
              </div>

              {/* Quick Stats / Gap Preview */}
              {gapAnalysis && gapAnalysis.identified_gaps.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Quick Insights
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3">
                      <div className="text-xs text-slate-500 mb-1">Top Gaps</div>
                      <div className="flex flex-wrap gap-1">
                        {gapAnalysis.identified_gaps.slice(0, 3).map((g, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-white border border-slate-200 rounded-lg text-slate-700">
                            {g}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3">
                      <div className="text-xs text-slate-500 mb-1">Enrolled</div>
                      <div className="text-sm font-semibold text-slate-800">
                        {gapAnalysis.enrolled_courses.length} courses
                      </div>
                      <div className="text-xs text-slate-400">
                        {gapAnalysis.enrolled_courses.reduce((acc, c) => acc + (c.progress_percent || 0), 0) / (gapAnalysis.enrolled_courses.length || 1)}% avg progress
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider text-center">
                  Try asking
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setInput(s);
                        inputRef.current?.focus();
                      }}
                      className="text-left px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Form */}
          <AnimatePresence>
            {showOnboarding && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-lg mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-amber-500" />
                  <h3 className="font-bold text-slate-900">Set Your Career Goal</h3>
                </div>
                <form onSubmit={submitGoal} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      name="current_role"
                      label="Current Role"
                      placeholder="e.g. Data Analyst"
                      required
                    />
                    <Input
                      name="target_role"
                      label="Target Role"
                      placeholder="e.g. ML Engineer"
                      required
                    />
                  </div>
                  <Input
                    name="years_experience"
                    label="Years of Experience"
                    type="number"
                    step="0.5"
                    placeholder="e.g. 2"
                  />
                  <Input
                    name="career_goal_summary"
                    label="Career Goal (optional)"
                    placeholder="I want to switch to AI within 6 months..."
                    multiline
                  />
                  <div className="flex gap-2">
                    <Button type="submit" leftIcon={<Sparkles className="w-4 h-4" />}>
                      Save Goal
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowOnboarding(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat Messages */}
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0 mt-1">
                  <Sparkles className="w-4 h-4" />
                </div>
              )}

              <div className={`max-w-[85%] sm:max-w-[75%] space-y-2 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-amber-500 text-white rounded-br-md'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>

                {/* Course Recommendations */}
                {msg.role === 'assistant' && msg.referenced_courses && msg.referenced_courses.length > 0 && (
                  <div className="space-y-2">
                    {msg.referenced_courses.map((course) => (
                      <button
                        key={course.id}
                        onClick={() => navigate(`/courses/${course.slug}`)}
                        className="flex items-center gap-3 w-full text-left bg-white border border-slate-200 hover:border-amber-300 hover:shadow-sm rounded-xl p-3 transition-all"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
                          <BookOpen className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800 truncate">{course.title}</div>
                          <div className="text-xs text-amber-600 font-medium">₹{course.price} — View Course</div>
                        </div>
                        <TrendingUp className="w-4 h-4 text-slate-300 shrink-0 ml-auto" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 shrink-0 mt-1">
                  <User className="w-4 h-4" />
                </div>
              )}
            </motion.div>
          ))}

          {/* Loading Indicator */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}

          {isLoadingHistory && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-4"
            >
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm text-red-700 mb-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
                <button
                  onClick={() => setError('')}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <div className="border-t border-slate-200 bg-white p-4 shrink-0">
          <div className="max-w-3xl mx-auto flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Mentor about your career, courses, interview prep..."
                rows={1}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none max-h-32 min-h-[44px]"
                style={{ height: 'auto' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = target.scrollHeight + 'px';
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 disabled:hover:bg-amber-500 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-center text-[10px] text-slate-400 mt-2">
            Mentor can make mistakes. Verify critical career decisions independently.
          </p>
        </div>
      </div>
    </div>
  );
}
